'use strict';

define('vb/private/services/definition/pluginServiceDefinition',[
  'vb/private/services/endpoint',
  'vb/private/services/serviceUtils',
  'vb/private/services/swaggerUtils',
  'vb/private/services/definition/definitionObject',
  'vb/private/services/readers/openApi3Object',
], (
  Endpoint,
  ServiceUtils,
  SwaggerUtils,
  DefinitionObject,
  OpenApi3Object,
) => {
  /**
   * @typedef {Object} Server
   * @property {(function(*): string)} getUrl
   * @property {Object} [variables]
   */

  /**
   * @typedef {Object} Backend
   * @property {string} name
   * @property {boolean} [extensionAccess]
   * @property {string} serviceType
   * @property {Server[]} servers
   */

  /**
   * @typedef {Object} CatalogInfo
   * @property {string} url
   * @property {string} namespace
   * @property {string} version
   * @property {Object[]} chain
   * @property {Object} backends
   * @property {Object} services
   * @property {Object} services.extensions
   * @property {Object} services.extensions.headers
   * @property {string} services.extensions.serviceType
   * @property {Object} services.metadata
   * @property {Object} metadata
   * @property {Object} mergedExtensions
   * @property {string} mergedExtensions.serviceName
   * @property {string} mergedExtensions.serviceType
   * @property {Object} mergedExtensions.headers
   * @property {string} mergedExtensions.resolvedProxyUrl
   * @property {string} mergedExtensions.resolvedTokenRelayUrl
   * @property {boolean} mergedExtensions.trapEnabled
   */

  /**
   * @typedef {import('vb/private/services/endpointReference').default} EndpointReference
   * @typedef {import('vb/private/services/protocolRegistry').default} ProtocolRegistry
   * @typedef {import('vb/private/services/readers/openApiObjectCommon').default} OpenApiObjectCommon
   */

  /**
   * ServiceDefinition backed by the programmatic endpoint plugin.
   */
  class PluginServiceDefinition extends DefinitionObject {
    /**
     *
     * @param {Backend} serviceBackend Definition of the backend providing this service data
     * @param {Object} plugin Plugin responsible for converting endpoint Ids to OpenApi like endpoint defs
     * @param {CatalogInfo} catalogInfo catalog.json information, if any
     * @param {ProtocolRegistry} protocolRegistry utility that handles vb-specific protocols
     */
    constructor(
      serviceBackend,
      plugin,
      requestInit,
      catalogInfo,
      protocolRegistry,
    ) {
      // Service class creates one service definition for the given service name in the extension.
      // That means that we will create one PluginServiceDefinition for multiple service IDs if those
      // service IDs contain module names: "businessObjects#crm", "businessObjects#hcrm". In this example
      // "businessObjects" is the common service name (i.e. programmatic endpoint's backend name).

      // this will be "businessObjects" for  "@base:businessObjects#crm/orders_getall" endpoint
      const { name } = serviceBackend;
      super(
        name,
        {}, // extensions - DefinitionObject will get them from catalogInfo.backends.extensions
        null, // parent
        catalogInfo.namespace,
        '', // relativePath
        catalogInfo,
        true, // isUnrestrictedRelative
      );

      this._plugin = plugin;
      this._serviceType = serviceBackend.serviceType;

      this._requestInit = requestInit;
      this._protocolRegistry = protocolRegistry;

      // setup properties (almost) the same way the ServiceDefinition does it

      this._server = {
        // the URL is already resolved
        getUrl: () => this.catalogInfo.url,
      };

      this._nameForProxy = ServiceUtils.getNameForProxy(name, /* path */ '', catalogInfo.chain);

      // we need the augmented info for the 'legacy' syntax, also, for http/https promotion via proxy
      this.extensions = ServiceUtils.augmentExtension(this.nameForProxy, catalogInfo, this.extensions);

      this._endpoints = {};
    }

    get server() {
      return this._server; // called by new EndpointMetadata
    }

    get requestInit() {
      return this._requestInit;
    }

    get nameForProxy() {
      return this._nameForProxy; // called by Endpoint.getAllHeaders
    }

    /**
     * Gets JSON Object representing OpenApi document defining the service.
     *
     * The following is disabled:
     * If endpointReference parameter is provided, the returned document will have references resolved
     * in the operation object corresponding to the endpointReference.
     *
     * @param {EndpointReference} [endpointReference]
     * @returns {Promise<Object>} OpenApi JSON document
     */
    getDefinition(endpointReference) {
      return Promise.resolve()
        .then(() => (endpointReference && this.findEndpoint(endpointReference)))
        .then((endpoint) => {
          if (endpoint) {
            return SwaggerUtils.createSwaggerFromEndpoint(endpoint);
          }
          return null;
        });
    }

    /**
     * Programmatic endpoint is extension accessible if the backend it references is extension accessible.
     *
     * @return {boolean} true if this backend definition is visible outside the container that defines it
     *                   (i.e., if an extension can use it).
     */
    isExtensionAccessible() {
      const backend = this._catalogInfo.chain[0];
      return (backend && backend.extensionAccess) === true;
    }

    getExtensionProperty(propertyName) {
      const mergedExtensions = this._catalogInfo && this._catalogInfo.mergedExtensions;
      return mergedExtensions && mergedExtensions[propertyName];
    }

    /**
     *
     * @param {EndpointReference} endpointRef Endpoint reference
     * @param {Object} [serverVariables]
     * @returns {Endpoint}
     */
    findEndpoint(endpointRef, serverVariables) {
      const key = this.getEndpointReferenceHash(endpointRef);

      if (this._endpoints[key] === undefined) {
        this._endpoints[key] = Promise.resolve()
          .then(() => this._getEndpointFromPlugin(endpointRef, serverVariables))
          .then(({
            operation, path, method, extensions, parameters,
          } = {}) => {
            if (operation) { // operationObject definition
              // mock OpenApi object in order to create OperationObject
              const openApi = /** @type {OpenApiObjectCommon} */ ({
                getExtensions: () => (extensions || {}),
              });
              const pathObject = {
                parameters: parameters || [],
              };
              const operationObj = new OpenApi3Object.OpenApi3OperationObject(method, pathObject, operation, openApi);
              const options = {
                name: endpointRef.operationId,
                service: this,
                protocolRegistry: this._protocolRegistry,
                pathKey: path,
                pathObject,
                operationKey: method,
                operationObject: operationObj,
                isUnrestrictedRelative: this.isUnrestrictedRelative, // ?? does this apply in this context?
              };
              // create new Endpoint from def
              return new Endpoint(options);
            }
            return null;
          });
      }
      return this._endpoints[key];
    }

    // eslint-disable-next-line class-methods-use-this
    getEndpointReferenceHash(endpointRef) {
      return `${endpointRef.operationId}#${endpointRef.moduleName}$${endpointRef.containerNamespace}`;
    }

    /**
     * @private
     */
    _getEndpointFromPlugin(endpointRef, serverVariables = {}) {
      // do we get variables for all servers in the backend chain or just the last one?
      const backendConfig = this.catalogInfo.chain[0];
      const resolvedVariables = (backendConfig && backendConfig.variables) || {};
      const allVariables = Object.assign({}, resolvedVariables, serverVariables);

      const server = {
        url: this.server.getUrl(),
        extensions: this.extensions,
      };

      const { moduleName, endpointName } = endpointRef;

      const options = {
        contextNamespace: endpointRef.containerNamespace,
        namespace: this.namespace,
        serviceId: endpointRef.serviceId,
        moduleName,

        // endpointRef may only contain serviceType so we need to get the name from the resolved backend object
        backendName: backendConfig.name,

        serverVariables: allVariables,

        url: this.catalogInfo.url,
        server,
      };

      // We create all EndpointReferences through EndpointReferencesFactory which calls the plugin
      // at that time to correctly split endpoint reference

      const endpointId = endpointName || endpointRef.operationId;

      const dynamicDefinition = this._plugin.getEndpointDefinition(endpointId, options);
      Object.assign(options, dynamicDefinition && dynamicDefinition.operation);

      endpointRef.adjustEndpoint(options);
      return dynamicDefinition;
    }
  }

  return PluginServiceDefinition;
});

